import React, { useEffect } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { COLORS_ADMIN_PAGE, ResizeHandle } from '../conf/constants';
import { ORDER_MANAGEMENT_CONSTANTS } from './constants/orderManagement';
import { useErrorHandler } from '../utils/useErrorHandler';
import { useOrderManagement } from './hooks/useOrderManagement';
import { useResizeHandle } from './hooks/useResizeHandle';
import UserSidebar from './components/UserSidebar';
import OrdersContent from './components/OrdersContent';
import { User } from '../user/types/type';

const OrderManagement: React.FC = () => {
  const { snackbar, handleSnackbarClose } = useErrorHandler();
  const { sidebarWidth, isResizing, containerRef, handleMouseDown } = useResizeHandle();
  
  const {
    // User data
    users,
    selectedUser,
    setSelectedUser,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    totalUsers,
    usersPerPage,
    
    // Order data
    orders,
    orderSearchTerm,
    setOrderSearchTerm,
    currentOrderPage,
    setCurrentOrderPage,
    totalOrders,
    ordersPerPage,
    
    // Functions
    fetchUsers,
    fetchOrders,
    onUsersPerPageChange,
    onOrdersPerPageChange, 
  } = useOrderManagement();

  // Effects
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, usersPerPage]); 

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedUser?.userId) {
      setCurrentOrderPage(1);
      fetchOrders(selectedUser.userId);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser?.userId) {
      fetchOrders(selectedUser.userId);
    }
  }, [currentOrderPage, orderSearchTerm, ordersPerPage]);

  // Handlers
  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, Math.ceil(totalUsers / usersPerPage))));
  };

  const handleOrderPageChange = (page: number) => {
    setCurrentOrderPage(Math.max(1, Math.min(page, Math.ceil(totalOrders / ordersPerPage))));
  };

  return (
    <Box
      sx={{ 
        display: 'flex', 
        bgcolor: COLORS_ADMIN_PAGE.BACKGROUND, 
        height: ORDER_MANAGEMENT_CONSTANTS.CONTAINER_HEIGHT 
      }}
      ref={containerRef}
      style={{ cursor: isResizing ? 'col-resize' : 'auto' }}
    >
      {/* Left Sidebar - User List */}
      <UserSidebar
        users={users}
        selectedUser={selectedUser}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        currentPage={currentPage}
        totalUsers={totalUsers}
        usersPerPage={usersPerPage}
        sidebarWidth={sidebarWidth}
        onUserSelect={handleUserSelect}
        onPageChange={handlePageChange}
        onUsersPerPageChange={onUsersPerPageChange}
      />

      {/* Resize Handle */}
      <ResizeHandle onMouseDown={handleMouseDown} />

      {/* Right Content - Orders */}
      <Box sx={{ flex: 1, bgcolor: COLORS_ADMIN_PAGE.WHITE, display: 'flex', flexDirection: 'column' }}>
        <OrdersContent
          selectedUser={selectedUser}
          orders={orders}
          orderSearchTerm={orderSearchTerm}
          setOrderSearchTerm={setOrderSearchTerm}
          currentOrderPage={currentOrderPage}
          totalOrders={totalOrders}
          ordersPerPage={ordersPerPage}
          onOrderPageChange={handleOrderPageChange}
          onOrdersPerPageChange={onOrdersPerPageChange}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={ORDER_MANAGEMENT_CONSTANTS.SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement;